import { HERO_BADGE_POSITIONS, ruStepIllustrations } from "./shared";
import { getScriptarioLocalizedPath } from "@/config/locales";
import type { LandingContent } from "./types";
import { intlFileHostingCopy } from "@/lib/file-hosting-sources-intl";

const locale = "es" as const;

const seoFaqText = `Scriptario convierte audio y video en texto editable con etiquetas de hablante, marcas de tiempo, resúmenes y exportaciones. Sirve para reuniones, entrevistas, clases, podcasts, llamadas y videos largos que necesitan convertirse en material de trabajo. Sube un archivo o pega un enlace compatible, deja que la IA transcriba la grabación y continúa con revisión, resumen, chat con IA o descarga del resultado.`;

export const scriptarioEsContent: LandingContent = intlFileHostingCopy({
  nav: {
    pageLinks: [
      { label: "Funciones", href: getScriptarioLocalizedPath("/features", locale) },
      { label: "Precios", href: getScriptarioLocalizedPath("/pricing", locale) },
      { label: "Blog", href: getScriptarioLocalizedPath("/blog", locale) },
      { label: "Contacto", href: getScriptarioLocalizedPath("/contacts", locale) },
    ],
    landingLinks: [
      { label: "Funciones", href: "#features" },
      { label: "Precios", href: "#pricing" },
      { label: "Blog", href: getScriptarioLocalizedPath("/blog", locale) },
      { label: "Contacto", href: getScriptarioLocalizedPath("/contacts", locale) },
    ],
  },
  header: {
    dashboard: "Panel",
    signIn: "Iniciar sesión",
    startFree: "Empezar gratis",
    openMenu: "Abrir menú",
    closeMenu: "Cerrar menú",
    menu: "Menú",
  },
  hero: {
    title: <>Transcripción de audio y video<br className="hidden desktop:inline" /> a texto con <span style={{ color: "var(--brand-primary)" }}>IA</span></>,
    subtitle: <>Llamadas, podcasts, clases, entrevistas y webinars convertidos en texto listo<br className="hidden desktop:inline" /> en minutos. Pruébalo gratis</>,
    badges: ["99% de precisión", "Marcas de tiempo", "Cualquier formato", "Chat e informes con IA", "Etiquetas de hablante", "1 hora a texto en 3 min"].map((label, i) => ({ label, ...HERO_BADGE_POSITIONS[i] })),
    trustText: "Grandes empresas confían en nosotros",
  },
  upload: {
    fileTab: "Archivo",
    linkTab: "Enlace",
    dragOrUpload: "Arrastra o sube un archivo",
    chooseFiles: "Elegir archivos",
    submit: "Enviar",
    removeFile: "Eliminar archivo",
    tabsLabel: "Método de carga",
    linkPlaceholder: "Pega un enlace de YouTube, Zoom o Google Drive...",
    linkPlaceholderMobile: "Pega un enlace",
    supportedSources: "Compatible con:",
    supportedFiles: "Se admiten archivos de audio y video en cualquier formato",
    comingSoon: "Scriptario estará disponible pronto",
    unsupportedFile: "Solo se admiten archivos de audio y video",
    uploadError: "Error al subir el archivo",
    retryUpload: "Intentar de nuevo",
    finishingUpload: "Finalizando la subida",
  },
  cookie: {
    textLine1: "Usamos cookies necesarias para que el sitio funcione.",
    textLine2: "Las cookies opcionales se usan solo con tu consentimiento.",
    accept: "Aceptar",
    reject: "Rechazar",
  },
  how: {
    title: "Cómo funciona",
    subtitle: "De la grabación al texto final en tres pasos simples",
    steps: [
      { step: "Paso 1", title: "Sube una grabación", description: "Arrastra un archivo de audio o video en cualquier formato. También puedes pegar un enlace de YouTube, Zoom o Google Drive.", illustration: ruStepIllustrations[0], className: "" },
      { step: "Paso 2", title: "La IA transcribe en minutos", description: "La IA reconoce el habla, añade puntuación, separa hablantes y crea marcas de tiempo. Una hora de audio tarda solo 3 minutos.", illustration: ruStepIllustrations[1], className: "overflow-hidden" },
      { step: "Paso 3", title: "Trabaja con el resultado", description: "Edita el texto en el navegador, pide a la IA un informe o descarga el archivo como PDF, Word, TXT o subtítulos SRT.", illustration: ruStepIllustrations[2], className: "overflow-hidden" },
    ],
  },
  stats: {
    users: "usuarios ya trabajan con nosotros",
    hours: "horas transcritas",
    rating: "calificación promedio de usuarios",
    speed: "trabajo más rápido con grabaciones",
  },
  features: {
    title: "Funciones",
    subtitle: "Todo lo necesario para trabajar con audio y video",
    cards: [
      { iconName: "feature-target", title: "99% de precisión", description: "La IA crea resúmenes, ideas clave y listas de tareas a partir de la transcripción cuando lo necesitas." },
      { iconName: "feature-speedometer", title: "Procesamiento rápido", description: "Una hora de grabación se transcribe en solo 3 minutos. Sin esperar horas." },
      { iconName: "feature-translate", title: "99 idiomas", description: "Inglés, español, alemán, francés y 95 idiomas más con detección automática." },
    ],
    aiReports: { title: "Informes con IA", description: "Resúmenes, ideas clave y tareas. La IA crea un informe a partir de tu transcripción." },
    aiChat: { title: "Chat con IA", description: "Haz preguntas sobre la transcripción. La IA encuentra la información correcta y responde directamente." },
    speakers: { title: "Separación de hablantes", description: "Detecta automáticamente quién habla. Cada línea queda etiquetada y con marca de tiempo." },
    meetingAssistant: { title: "Asistente de reuniones", description: "Graba llamadas de Google Meet, Zoom u otras plataformas. El asistente transcribe y prepara notas automáticamente." },
    telegramBot: { title: "Versión móvil", description: "Abre Scriptario en tu teléfono para subir grabaciones, revisar transcripciones y trabajar con los resultados estés donde estés." },
    linkTranscription: { title: "Transcripción por enlace", description: "Pega un enlace de YouTube, de una grabación de Zoom o de Google Drive y lo descargaremos y lo transcribiremos automáticamente." },
    smallCards: [
      { iconName: "feature-text-aa", title: "Puntuación automática", description: "Puntos, comas y párrafos se añaden automáticamente. El texto queda listo para leer." },
      { iconName: "feature-file", title: "Todos los formatos", description: "MP3, WAV, MP4, MOV, MKV, OGG, M4A y muchos más." },
      { iconName: "feature-download", title: "Exportación a formatos", description: "Descarga transcripciones como PDF, DOCX, TXT o subtítulos SRT." },
    ],
  },
  personas: {
    title: "Para quién es",
    subtitle: "Equipos de muchos ámbitos ya ahorran horas cada semana",
    items: [
      { image: "/assets/personas/manager.png", title: "Gerentes y líderes de equipo", description: "Notas automáticas de reuniones y resúmenes de reuniones diarias. La IA resalta decisiones y tareas." },
      { image: "/assets/personas/entrepreneur.png", title: "Emprendedores", description: "Transcribe llamadas con clientes, entrevistas de descubrimiento y negociaciones. Guarda cada detalle importante en texto buscable." },
      { image: "/assets/personas/creator.png", title: "Creadores de contenido", description: "Convierte podcasts y videos en publicaciones, artículos y guiones. Genera subtítulos para contenido sin sonido." },
      { image: "/assets/personas/researcher.png", title: "Investigadores y sociólogos", description: "Transcribe entrevistas en profundidad y grupos focales en minutos. Los informes con IA muestran ideas y temas clave." },
      { image: "/assets/personas/journalist.png", title: "Periodistas y medios", description: "Transcribe entrevistas y ruedas de prensa en pocos minutos. Las marcas de tiempo ayudan a encontrar citas rápido." },
      { image: "/assets/personas/student.png", title: "Estudiantes y docentes", description: "Convierte clases y seminarios en notas listas. Busca por palabra clave en vez de reproducir grabaciones." },
    ],
  },
  pricing: {
    title: "Transcribe horas de grabación en minutos",
    reviewLabel: "en Trustpilot",
    usersLabel: "usuarios",
    hoursLabel: "horas transcritas",
    month: "Mes",
    year: "Año",
    yearBadge: "-50%",
    periodYearTitle: "Anual",
    periodMonthTitle: "Mensual",
    yearTotalNote: "en un solo pago al año",
    plans: [
      { planId: "basic", title: "Essential", description: "para tareas regulares", ctaLabel: "Suscribirse" },
      { planId: "standard", title: "Professional", description: "para la mayoría de tareas", ctaLabel: "Suscribirse", featured: true, badge: "Popular" },
      { planId: "expert", title: "Premium", description: "para grandes volúmenes", ctaLabel: "Suscribirse" },
    ],
    tryTitle: "¿Solo quieres probar?",
    tryDescription: "Tienes hasta 15 minutos gratis por mes.",
    startFree: "Empezar gratis →",
    includedTitle: "Incluido en todos los planes:",
    commonFeatures: ["Grabación de reuniones", "Informes de IA y chat", "Archivos de hasta 5 GB", "Hablantes y marcas de tiempo"],

    commonFeatureIcons: ["meetings", "ai", "files", "speakers"],
    cancel: "Cancela cuando quieras",
    refund: "Política de reembolso de 7 días",
    perMonth: "/mes",
    perYear: "/año",
    annualBilling: "facturado anualmente",
    monthlyBilling: "Facturación mensual",
    overagePrefix: "por encima del límite — ",
    overageSuffix: "/min",
    minutesPerMonth: (minutes) => `${minutes.toLocaleString("es-ES")} minutos al mes`,
  },
  testimonials: {
    title: "Reseñas",
    subtitle: "Lo que dicen nuestros usuarios",
    items: [
      { quote: "Transcribo 3 o 4 entrevistas por semana. Antes tardaba más de una hora en pasar una llamada a texto. Ahora tengo el texto en minutos y veo claramente quién dijo qué.", avatar: "/assets/testimonial-1.jpg", name: "Sarah Mitchell", role: "Editora de revista digital" },
      { quote: "Grabo clases y las subo al servicio. En pocos minutos recibo una transcripción completa con párrafos y puntuación. Puedo reutilizar el material para mis notas.", avatar: "/assets/testimonial-2.jpg", name: "Emily Carter", role: "Profesora de comunicación" },
      { quote: "Uso Scriptario para actas de reuniones y llamadas con clientes. La IA destaca tareas y decisiones automáticamente. Ahorra varias horas de documentación cada semana.", avatar: "/assets/testimonial-3.jpg", name: "Michael Brooks", role: "Responsable de proyectos en una empresa SaaS" },
    ],
  },
  faq: {
    title: "Preguntas frecuentes",
    subtitle: "Respuestas clave sobre el servicio y el flujo de trabajo",
    items: [
      { question: "¿Trabajan con empresas y equipos?", answer: "Sí. Ayudamos a equipos de investigación y empresas a elegir el volumen y la configuración de transcripción adecuados. Para hablar de detalles, escribe a hello@scriptario.com." },
      { question: "¿Qué idiomas admite Scriptario?", answer: "La transcripción está disponible en 99 idiomas, incluidos inglés, español, alemán, francés y muchos más. El idioma se detecta automáticamente." },
      { question: "¿Cómo se etiquetan los hablantes?", answer: "El servicio detecta y etiqueta hablantes automáticamente. Si hace falta, puedes editar los nombres manualmente en el editor." },
      { question: "¿Cómo protegen mis archivos y datos?", answer: "Los archivos y transcripciones se cifran y se almacenan en infraestructura protegida. No vendemos tus datos." },
      { question: "¿Hay límites de tamaño o duración?", answer: "Puedes subir archivos de hasta 5 GB. La duración máxima de una grabación es de 12 horas." },
      { question: "¿A qué formatos puedo exportar?", answer: "Las transcripciones terminadas se pueden exportar como TXT. Otros formatos forman parte del flujo del producto." },
      { question: "¿Qué es Scriptario y cómo funciona?", answer: seoFaqText },
    ],
  },
  cta: {
    titleLine1: "Prueba Scriptario",
    titleLine2: "gratis",
    subtitleLine1: "15 minutos gratuitos de transcripción.",
    subtitleLine2: "Sin tarjeta y sin compromiso",
    button: "Empezar gratis",
  },
  footer: {
    tagline: "Transcripción de audio y video con IA",
    copyright: "2026 Scriptario. Todos los derechos reservados.",
    columns: [
      { title: "Producto", links: [{ label: "Funciones", href: getScriptarioLocalizedPath("/features", locale) }, { label: "Precios", href: getScriptarioLocalizedPath("/pricing", locale) }, { label: "Blog", href: getScriptarioLocalizedPath("/blog", locale) }, { label: "Contacto", href: getScriptarioLocalizedPath("/contacts", locale) }] },
      { title: "Soporte", links: [{ label: "WhatsApp", href: "https://wa.me/447732727913?text=%C2%A1Hola%21%20Necesito%20ayuda%20con%20Scriptario." }] },
      { title: "Legal", links: [{ label: "Política de privacidad", href: getScriptarioLocalizedPath("/privacy-policy", locale) }, { label: "Términos de servicio", href: getScriptarioLocalizedPath("/terms", locale) }, { label: "Política de reembolso", href: getScriptarioLocalizedPath("/refund", locale) }, { label: "Política de cookies", href: getScriptarioLocalizedPath("/cookie-policy", locale) }] },
    ],
  },
});
